// One Org Chart position record ("role") drives the Org Chart tree, the Roles
// table and the role column on Users. These helpers keep the three views in step.

export const DEFAULT_ORG_LEVELS = [
  { rank: 0, label: 'Executive', color: '#7c6fd6' },
  { rank: 1, label: 'C-Level', color: '#9b8fe8' },
  { rank: 2, label: 'Directors', color: '#3fa98a' },
  { rank: 3, label: 'Team Leads', color: '#d39a3a' },
  { rank: 4, label: 'Staff', color: '#8a90a0' },
];

export const TOP_OF_CHART = '__root__';
export const UNPLACED = '__unplaced__';

export const levelMeta = (levels, rank) => {
  const list = levels?.length ? levels : DEFAULT_ORG_LEVELS;
  return list[Math.min(Math.max(Number(rank) || 0, 0), list.length - 1)];
};

// Tinted chip colours derived from the level colour so any tenant palette stays readable.
export const levelChipStyle = (color) => ({
  background: `color-mix(in srgb, ${color} 16%, var(--qflow-surface, #fff))`,
  color: `color-mix(in srgb, ${color} 70%, var(--qflow-text, #1e1a33))`,
});

export const isVacant = (position) => !position?.user_id;

export const placementOf = (position) => {
  if (position?.parent_id) return 'placed';
  if (position?.placement === 'root' || position?.placement === 'unplaced') return position.placement;
  return Number(position?.level || 0) === 0 ? 'root' : 'unplaced';
};

export const rolesByUser = (positions) => positions.reduce((map, position) => {
  if (position.user_id) (map[position.user_id] = map[position.user_id] || []).push(position);
  return map;
}, {});

// Vacant roles first, then by level and title; used wherever a role is picked.
export const sortRolesForPicking = (positions) => [...positions].sort((left, right) =>
  Number(isVacant(right)) - Number(isVacant(left))
  || Number(left.level || 0) - Number(right.level || 0)
  || (left.title || '').localeCompare(right.title || ''));

export const descendantIds = (positions, rootId) => {
  const children = positions.reduce((map, position) => {
    if (position.parent_id) (map[position.parent_id] = map[position.parent_id] || []).push(position.id);
    return map;
  }, {});
  const found = new Set();
  const stack = [...(children[rootId] || [])];
  while (stack.length) {
    const id = stack.pop();
    if (found.has(id)) continue;
    found.add(id);
    stack.push(...(children[id] || []));
  }
  return found;
};

// Roles a given role may report to: anything except itself and its own reports.
export const validManagers = (positions, positionId) => {
  if (!positionId) return positions;
  const blocked = descendantIds(positions, positionId);
  blocked.add(positionId);
  return positions.filter((position) => !blocked.has(position.id));
};

export const buildTree = (positions) => {
  const nodes = Object.fromEntries(positions.map((position) => [position.id, { ...position, children: [] }]));
  const roots = [];
  positions.forEach((position) => {
    const node = nodes[position.id];
    if (position.parent_id && nodes[position.parent_id]) nodes[position.parent_id].children.push(node);
    else if (placementOf(position) === 'root') roots.push(node);
  });
  const sortChildren = (node) => {
    node.children.sort((a, b) => (a.order || 0) - (b.order || 0) || (a.title || '').localeCompare(b.title || ''));
    node.children.forEach(sortChildren);
  };
  roots.forEach(sortChildren);
  roots.sort((a, b) => (a.level || 0) - (b.level || 0) || (a.order || 0) - (b.order || 0));
  return roots;
};

export const initials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '?';

export const apiError = (error, fallback) => error?.response?.data?.detail || fallback;
